import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useRecreateOrder } from '#src/hooks';
import { selectParams } from '#src/store/location/selectors';
import { decode } from '#src/lib/base64';

// "Unauthorized access. user_id: 1000014430, sub: 1000014431. user_id must be equal to jwt sub, or role must be 'admin' or 'service'."
function isDifferentUserError(error) {
  return error?.message?.includes('user_id must be equal to jwt sub');
}

// extract and decode the order item inputs from the URL
function useOrderItemInputs(param = 'oii') {
  const { [param]: oii } = useSelector(selectParams);
  return useMemo(() => {
    try {
      return oii != null ? decode(oii) : {};
    } catch {
      return {};
    }
  }, [oii]);
}

/**
 * Checks for a "different user"/auth error and if found recreates the order (via redirect) for the current user session
 * See WBRT-2171
 *
 * @param {object} params
 * @param {object[]} params.errors - The errors to check for a "different user" error
 * @param {string|number} params.orderId - The id of the original order; only used for logging; may be omitted
 */
export function useRecreateOrderOnAuthError({ errors, orderId }) {
  const orderItemInputs = useOrderItemInputs();
  const recreateOrder = useRecreateOrder(orderItemInputs, orderId);
  const hasDifferentUserError = useMemo(
    () => errors?.some(isDifferentUserError),
    [errors]
  );

  if (hasDifferentUserError) {
    recreateOrder();
  }
}
